/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains functions to initialise the client application.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a react pure component.
 */
import * as React from 'react';

/*
 * Used to provide the application with access to the style theme.
 */
import { ThemeProvider } from 'react-jss';

/*
 * Used to create a history object to store routing history.
 */
import { createBrowserHistory } from 'history';

/*
 * Used to create the redux store to be used in the application.
 */
import createReduxStore from './store/createStore';
import { createJssInstance } from './styles/jssInstance';
import { createGenerateClassName } from './styles/createGenerateClassNames';
import * as Tracking from './utils/common/analytics';
import { dom as fontAwesome } from '@fortawesome/fontawesome-svg-core';

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import * as Sentry from "@sentry/react";

/*
 * The theme that will be provided to the application.
 */
import DefaultTheme from './styles/themes/defaultTheme.jss';

/*
 * The react application that will be rendered.
 */
import App from './ConnectedApp';

/*
 * Used to provide the app with access to the redux store.
 */
import { Provider } from 'react-redux';
import ReactAI from 'react-appinsights';
import { ConnectedRouter } from 'connected-react-router';
import { render } from 'react-dom';
import { AppSettings } from './AppSettings';
import { Router } from 'react-router';
import { Person } from './dtos/FaceMatch.dtos';
import { createSentryAnonymousUser } from './utils/common/errors';
/*
 * Used to provide the app with access to a configured jss instance.
 */
const JssProvider = require('react-jss').JssProvider;

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

/**
 * Application wrapper component properties.
 */
interface IAppWrapperProps {}

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const css = fontAwesome.css();
const head = document.head || document.getElementsByTagName('head')[0];
const style = document.createElement('style');
head.appendChild(style);
style.type = 'text/css';
style.appendChild(document.createTextNode(css));
const history = createBrowserHistory();
Tracking.initGa((window as any).GOOGLE_ANALYTICS_ID, (window as any).GOOGLE_ANALYTICS_DEBUG, history);
const {
  store
} = createReduxStore(history);
const appInsightsInstrumentationKey: any = (window as any).APP_INSIGHTS_INSTRUMENTATION_KEY;
if (appInsightsInstrumentationKey) {
  ReactAI.init({
    instrumentationKey: appInsightsInstrumentationKey
  }, history);
}
const jssInstance = createJssInstance();
const generateClassName = createGenerateClassName();
if (AppSettings.sentry.dsn) {
  const integrations = [
  // React Router V6 integration.
  // Helps with tracing URL changes and which URLs belong to the 
  // same route, for example:
  //    - https://test.com/form/1
  //    - https://test.com/form/2
  // both belong to the same route */form/*
  Sentry.reactRouterV4BrowserTracingIntegration({
    history
  }),
  // Browser Tracing integration
  // Helps with tracing URLs and other common browser "spans"
  Sentry.browserTracingIntegration(),
  // Replay integration
  // Records user actions and sends to Sentry.
  Sentry.replayIntegration({
    // Whether to mask all text on the page when recording user actions
    // When false only inputs are masked.
    // There is a separate setting to force unmasked inputs.
    maskAllText: false,
    // Whether to mask all images on the page when recording user 
    // actions.
    blockAllMedia: false,
    // Which URLs can the replay run on.
    // URL must contain one of the strings in the array or match one of
    // the regexes supplied
    networkDetailAllowUrls: [window.location.origin],
    // Capture the body of network requests
    // Sentry will still run data scrubbing on the bodies.
    networkCaptureBodies: true
  })];
  if (AppSettings.sentry.enableReportABug) {
    integrations.push(Sentry.feedbackIntegration({
      // Disable the injection of the default widget
      autoInject: false,
      colorScheme: 'system',
      showBranding: false,
      showName: false,
      showEmail: false,
      tags: {
        Application: AppSettings.sentry.application,
        Customer: AppSettings.sentry.customer
      },
      useSentryUser: {
        name: 'fullName',
        email: 'email'
      }
    }));
  }
  Sentry.init({
    // The DSN to use when sending information to Sentry.
    // Identifies a specific project in Sentry.
    dsn: AppSettings.sentry.dsn,
    // Whether to send identifying information to Sentry such as
    // username, useragent, ip, etc
    sendDefaultPii: AppSettings.sentry.sendDefaultPii,
    // Whether to sent available performance metrics to Sentry.
    // These are the basic performance metrics and not the endepth
    // profiling metrics that take a lot more effort to setup.
    enableMetrics: AppSettings.sentry.enableMetrics,
    // Whether to attach stacktraces to actions sent to Sentry.
    attachStacktrace: AppSettings.sentry.attachStacktrace,
    // Whether @sentry/react is in debug mode, logging more information
    // to the console.
    debug: AppSettings.sentry.debug,
    // initialise integrations.
    integrations: integrations,
    // The environment to associate all action sent to Sentry with.
    environment: AppSettings.sentry.environment,
    // Release to associate all actions sent to Sentry with.
    release: AppSettings.sentry.release,
    // Percentage of captured traces that are sent to Sentry.
    // 1.0 = 100%, 0 = 0%
    tracesSampleRate: AppSettings.sentry.tracesSampleRate,
    // Which URLs to trace / record in a trace.
    // Only trace internal URLs don't want to trace every SEO url
    // under the sun.
    tracePropagationTargets: ['localhost', /^\//, new RegExp(`/^${window.location.origin}`)],
    // Percentage of captured session replays that are sent to Sentry.
    // 1.0 = 100%, 0 = 0%
    replaysSessionSampleRate: AppSettings.sentry.replaysSessionSampleRate,
    // Percentage of captured session replays that are sent to Sentry.
    // when an error occurs
    // 1.0 = 100%, 0 = 0%
    replaysOnErrorSampleRate: AppSettings.sentry.replaysOnErrorSampleRate,
    // Enable logs to be sent to Sentry.
    enableLogs: AppSettings.sentry.enableLogs
  });
  Sentry.setTag('Application', AppSettings.sentry.application);
  Sentry.setTag('Customer', AppSettings.sentry.customer);
  const person: Person | undefined = store.getState().authenticatedPerson?.data;
  if (person) {
    Sentry.setUser({
      id: `${person.id}`,
      username: `${person.firstName} ${person.lastName}`,
      fullName: `${person.firstName} ${person.lastName}`,
      email: `${person.email}`
    });
  } else {
    Sentry.setUser(createSentryAnonymousUser());
  }
}

/**
 * Application wrapper that provides the style theme to the rest of the application.
 */
class AppWrapper extends React.Component<IAppWrapperProps> {
  render() {
    return <Provider store={store} data-sentry-element="Provider" data-sentry-component="AppWrapper" data-sentry-source-file="index.tsx">
                <JssProvider jss={jssInstance} disableStylesGeneration={false} generateClassName={generateClassName} data-sentry-element="JssProvider" data-sentry-source-file="index.tsx">
                    <Sentry.ErrorBoundary fallback={<>An error has occurred. Please refresh the page or try again later.</>} data-sentry-element="Sentry.ErrorBoundary" data-sentry-source-file="index.tsx">
                        <ConnectedRouter history={history} data-sentry-element="ConnectedRouter" data-sentry-source-file="index.tsx">
                            <ThemeProvider theme={DefaultTheme} data-sentry-element="ThemeProvider" data-sentry-source-file="index.tsx">
                                <App data-sentry-element="App" data-sentry-source-file="index.tsx" />
                            </ThemeProvider>
                        </ConnectedRouter>
                    </Sentry.ErrorBoundary>
                </JssProvider>
            </Provider>;
  }
}
render(<AppWrapper />, document.getElementById('root'));

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default AppWrapper;